import { Head, useForm, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft } from 'lucide-react'
import { FormEvent } from 'react'

interface License {
  id: number
  agent_id: number
  state_code: string
  license_number: string
  status: string
  issued_at: string
  expires_at: string
}

interface Agent {
  id: number
  full_name: string
  email: string
}

interface SelectOption {
  id: number
  name: string
}

interface State {
  code: string
  name: string
}

interface Props extends PageProps {
  license: License | null
  agent: Agent | null
  agents: SelectOption[] | null
  states: State[]
}

export default function LicenseForm({ license, agent, agents, states }: Props) {
  const isEdit = !!license

  const { data, setData, post, put, processing, errors } = useForm({
    agent_id: license?.agent_id?.toString() || agent?.id?.toString() || '',
    state_code: license?.state_code || '',
    license_number: license?.license_number || '',
    status: license?.status || 'active',
    issued_at: license?.issued_at || '',
    expires_at: license?.expires_at || ''
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isEdit) {
      put(`/licenses/${license.id}`)
    } else if (agent) {
      post(`/agents/${agent.id}/licenses`)
    } else {
      post('/licenses')
    }
  }

  const backUrl = agent ? `/agents/${agent.id}/licenses` : '/licenses'

  return (
    <AppLayout>
      <Head title={isEdit ? 'Edit License' : 'New License'} />

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href={backUrl} className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit License' : 'New License'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          {!agent && agents && (
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Agent *</label>
              <select
                value={data.agent_id}
                onChange={(e) => setData('agent_id', e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select agent...</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {errors.agent_id && <p className="mt-1 text-sm text-status-error">{errors.agent_id}</p>}
            </div>
          )}

          {agent && (
            <div className="p-4 bg-chestnut-cream rounded-lg">
              <p className="text-sm text-chestnut-charcoal/60">Agent</p>
              <p className="font-medium text-chestnut-charcoal">{agent.full_name}</p>
              <p className="text-sm text-chestnut-charcoal/60">{agent.email}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">State *</label>
              <select
                value={data.state_code}
                onChange={(e) => setData('state_code', e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select state...</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>{state.code} - {state.name}</option>
                ))}
              </select>
              {errors.state_code && <p className="mt-1 text-sm text-status-error">{errors.state_code}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">License Number *</label>
              <input
                type="text"
                value={data.license_number}
                onChange={(e) => setData('license_number', e.target.value)}
                className="input-field w-full"
                required
              />
              {errors.license_number && <p className="mt-1 text-sm text-status-error">{errors.license_number}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Status *</label>
              <select
                value={data.status}
                onChange={(e) => setData('status', e.target.value)}
                className="input-field w-full"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>

            <div />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Issue Date</label>
              <input
                type="date"
                value={data.issued_at}
                onChange={(e) => setData('issued_at', e.target.value)}
                className="input-field w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Expiration Date</label>
              <input
                type="date"
                value={data.expires_at}
                onChange={(e) => setData('expires_at', e.target.value)}
                className="input-field w-full"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href={backUrl} className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : (isEdit ? 'Update License' : 'Create License')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
